import { apiClient, getErrorMessage } from '@/lib/apiClient';
import { OrgUnitMember, CreateOrgUnitMember, UpdateOrgUnitMember } from '@/types/core/OrgUnitMember';
import { AbpPagedRequest, AbpPagedResponse } from '@/lib/gridDataSourceUtils';

class OrgUnitMemberService {
  private readonly baseUrl = '/api/core/orgunitmember';

  async getList(params: AbpPagedRequest & { orgUnitId?: string; userId?: string }): Promise<AbpPagedResponse<OrgUnitMember>> {
    try {
      const response = await apiClient.get<AbpPagedResponse<OrgUnitMember>>(this.baseUrl, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching memberships:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async getById(id: string): Promise<OrgUnitMember> {
    try {
      const response = await apiClient.get<OrgUnitMember>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching membership:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async create(data: CreateOrgUnitMember): Promise<OrgUnitMember> {
    try {
      const response = await apiClient.post<OrgUnitMember>(this.baseUrl, data);
      return response.data;
    } catch (error) {
      console.error('Error creating membership:', error);
      throw error;
    }
  }

  async update(id: string, data: UpdateOrgUnitMember): Promise<OrgUnitMember> {
    try {
      const response = await apiClient.put<OrgUnitMember>(`${this.baseUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating membership:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error deleting membership:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async getByOrgUnit(orgUnitId: string): Promise<OrgUnitMember[]> {
    try {
      const response = await apiClient.get<OrgUnitMember[]>(`${this.baseUrl}/by-orgunit/${orgUnitId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching org unit members:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async getByUser(userId: string): Promise<OrgUnitMember[]> {
    try {
      const response = await apiClient.get<OrgUnitMember[]>(`${this.baseUrl}/by-user/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user memberships:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async getMyMemberships(): Promise<OrgUnitMember[]> {
    try {
      const response = await apiClient.get<OrgUnitMember[]>(`${this.baseUrl}/my-memberships`);
      return response.data;
    } catch (error) {
      console.error('Error fetching my memberships:', error);
      throw new Error(getErrorMessage(error));
    }
  }
}

export const orgUnitMemberService = new OrgUnitMemberService();
